import { blobToUrl } from '@Utils/helpers/imageHelpers';
import { SortOrder } from '@Utils/helpers/sortingHelpers';
import { getTimestampStr } from '@Utils/helpers/stringHelpers';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { Store } from 'redux';
import {
    mapWfsData,
    parseClientId,
} from '@Components/MapComponents/MapLibre/Utils';
import { BrowserPersistenceApi } from '@Data/browserPersistence/Api';
import { getUpdatedElapsedTime } from '@Data/browserPersistence/Helpers';
import { IPersistedQcRun } from '@Data/browserPersistence/Interfaces';
import { IEntity } from '@Data/entities/Interfaces';
import GeoserverApi from '@Data/geoserver/Api';
import { IGeoserverFeature } from '@Data/geoserver/Interfaces';
import {
    entityEndPoints,
    qualityControlEndpoints,
} from '@Services/data/ApiHandler';
import { error } from '@Services/notification/NotificationServices';
import store from '@Services/redux/Store';
import {
    GlobalAxiosCancelTokenKey,
    addQcCancelToken,
    cancelOutstandingQcRequests,
    cloneReasonAndAddComment,
    extractImagesFromPole,
    extractLabelInfoFromCenterPole,
    findAndUpdateUrl,
    getAttachedPoleBySpan,
    mapAttachedSpans,
    parseAttachedSpansAndPoles,
    shuffleAnalysisEntities,
} from './Helpers';
import {
    AnalysisEntityState,
    GetSpansResponse,
    IAnalysisEntity,
    IQcPole,
    IQcSpanEntity,
    IReworkReason,
    IReworkReasonStage,
    ImageIndex,
    QualityControlState,
    TableSortField,
} from './Interfaces';
import { QualityControlActions } from './Reducer';
import { initialQualityControlState } from './State';

export const QualityControlApi = {
    reset: () => {
        store.dispatch(QualityControlActions.reset());
    },
    initializeGeoserverData: async (
        projectId: number,
        folderIds: Array<string>
    ) => {
        const tokenKey = GlobalAxiosCancelTokenKey.GET_SVG_VIEWER_DATA;

        // cancel all outstanding tokens
        cancelOutstandingQcRequests(tokenKey);

        if (folderIds.length === 0) {
            store.dispatch(
                QualityControlActions.setGeoserverData({
                    features: [],
                    type: '',
                    totalFeatures: 0,
                    numberMatched: 0,
                    numberReturned: 0,
                    timeStamp: '',
                    crs: undefined,
                    bbox: undefined,
                })
            );
            return;
        }

        // create a new cancel token
        const cancelToken = addQcCancelToken(tokenKey);

        const response = await GeoserverApi.getWfsData(
            folderIds,
            projectId,
            cancelToken
        );
        if (response.data && response.data.features) {
            const mappedData = mapWfsData(response.data.features);
            const responseData = _cloneDeep(response.data);
            responseData.features = mappedData;
            store.dispatch(
                QualityControlActions.setGeoserverData(responseData)
            );
        }
    },
    setInspectionStartDate: (startDate: string) => {
        store.dispatch(QualityControlActions.setInspectionStartDate(startDate));
    },
    /**
     * @param {boolean} startQC Determines whether the QC process is started or not. Some features rely on parsed analysis entities outside of the QC process, eg. setting a folder's status.
     */
    initialize: (
        geoserverFeatures: IGeoserverFeature[],
        startQC = true,
        selectedFolderId?: string
    ) => {
        let persistedQcRun: IPersistedQcRun;
        if (selectedFolderId) {
            persistedQcRun = store
                .getState()
                .browserPersistence.qcRuns.find(
                    (q) => q.folder_id === selectedFolderId
                );
        }

        // map the features to be able to be QCed
        const mappedFeatures: IAnalysisEntity[] = geoserverFeatures.map(
            (feature) => {
                const versionId = feature.properties?.entity_version_id;

                const persistedPole = persistedQcRun?.data.find(
                    (f) => f.entity_version_id === versionId
                );

                return {
                    entity_version_id: versionId,
                    client_id: parseClientId(feature.properties?.client_id),
                    qcState: persistedPole?.qcState ?? AnalysisEntityState.TODO,
                    reworkReasons: persistedPole?.reworkReasons ?? [],
                    hasBaseData: false,
                };
            }
        );

        store.dispatch(
            QualityControlActions.updateAnalysisEntities(mappedFeatures)
        );

        if (startQC) {
            const folderId = store.getState().folder.selectedFolderId;

            const startDate = persistedQcRun
                ? new Date(persistedQcRun.startTimestamp).toISOString()
                : new Date().toISOString();

            const initialEntity = persistedQcRun?.selectedPoleEntityVersionId
                ? mappedFeatures.find(
                      (f) =>
                          f.entity_version_id ===
                          persistedQcRun?.selectedPoleEntityVersionId
                  )
                : mappedFeatures[0];

            QualityControlApi.setSelectedAnalysisEntity(initialEntity);

            store.dispatch(
                QualityControlActions.setInspectionStartDate(startDate)
            );

            store.dispatch(
                QualityControlActions.setState(QualityControlState.IN_PROGRESS)
            );

            if (_isNil(persistedQcRun)) {
                BrowserPersistenceApi.initialize(
                    folderId,
                    mappedFeatures,
                    startDate
                );
            } else {
                BrowserPersistenceApi.resume(folderId);
            }
        }

        return mappedFeatures;
    },
    setTableSorting: (field: TableSortField, order: SortOrder) => {
        store.dispatch(
            QualityControlActions.setTableSorting({
                field: field,
                order: order,
            })
        );
    },
    /**
     * @param {IAnalysisEntity | string} entity Either the full object, or its entity_version_id. Some components only have access to a partial entity, so they pass in the entity_version_id.
     * @param {boolean} callGetSpans determines whether to call the get_spans endpoint. Some components need the data of a mapped IAnalysisEntity outside of the QC view.
     */
    setSelectedAnalysisEntity: async (
        entity: IAnalysisEntity | string,
        callGetSpans = true
    ) => {
        let analysisEntity =
            typeof entity === 'string'
                ? store
                      .getState()
                      .qualityControl.analysisEntities.find(
                          (e) => e.entity_version_id === entity
                      )
                : entity;

        // dispatch first to update views
        store.dispatch(
            QualityControlActions.setSelectedAnalysisEntity(analysisEntity)
        );

        // get entity version details if not loaded
        if (!analysisEntity.hasBaseData) {
            const response = await entityEndPoints.getEntityVersionById(
                analysisEntity.entity_version_id
            );
            if (response.isSuccess) {
                const baseEntityVersion: IEntity = response.data;
                analysisEntity = {
                    ...baseEntityVersion,
                    ...analysisEntity,
                    hasBaseData: true,
                };

                store.dispatch(
                    QualityControlActions.setSelectedAnalysisEntity(
                        analysisEntity
                    )
                );
            }
        }

        if (callGetSpans) {
            QualityControlApi.getSpans(analysisEntity.entity_version_id);
        }

        return analysisEntity;
    },
    /**
     * Calls the get-spans-by-entity-version-v2 endpoint, parses the data, and downloads the images.
     */
    getSpans: async (versionId: string) => {
        store.dispatch(QualityControlActions.resetSvgViewer());

        const projectId = store.getState().project.selectedProject?.project_id;
        const tokenKey = GlobalAxiosCancelTokenKey.GET_SVG_VIEWER_DATA;

        // cancel all outstanding tokens
        cancelOutstandingQcRequests(tokenKey);

        // create a new cancel token
        const cancelToken = addQcCancelToken(tokenKey);

        const response = await qualityControlEndpoints.getSpans(
            versionId,
            projectId,
            cancelToken
        );

        if (response.isSuccess) {
            const data: GetSpansResponse = response.data.data;

            store.dispatch(QualityControlActions.setRawData(data));

            // create some new objects to map to
            const backpoleData: IQcPole = _cloneDeep(
                initialQualityControlState.backPole
            );
            const backspanData: IQcSpanEntity = _cloneDeep(
                initialQualityControlState.backSpan
            );
            let centerpoleData: IQcPole = _cloneDeep(
                initialQualityControlState.centrePole
            );
            const forespanData: IQcSpanEntity = _cloneDeep(
                initialQualityControlState.foreSpan
            );
            const forepoleData: IQcPole = _cloneDeep(
                initialQualityControlState.forePole
            );

            let allAttachedSpans = _cloneDeep(
                initialQualityControlState.allAttachedSpans
            );

            /**
             * Dispatch the parsed data to redux
             */
            const dispatchResults = () => {
                store.dispatch(
                    QualityControlActions.updateBackPoleData(backpoleData)
                );
                store.dispatch(
                    QualityControlActions.updateBackSpanData(backspanData)
                );
                store.dispatch(
                    QualityControlActions.updateForeSpanData(forespanData)
                );
                store.dispatch(
                    QualityControlActions.updateForePoleData(forepoleData)
                );
                store.dispatch(
                    QualityControlActions.updateAllAttachedSpans(
                        allAttachedSpans
                    )
                );
                store.dispatch(
                    QualityControlActions.updateImageLoadingByIndex({
                        indices: [0, 1, 2, 3, 4],
                        isLoading: false,
                    })
                );
            };

            const selectedPole = data.find((pole) => pole.pole === 'selected');

            // extract the images from the selected pole
            const selectedImageId = await extractImagesFromPole(
                selectedPole,
                centerpoleData,
                cancelToken,
                true
            );

            if (selectedImageId === null) {
                error(
                    'No image was found for this pole, please select another pole.'
                );
                dispatchResults();
                return;
            } else {
                // render the initial image, and start fetching the data for the outer entities.
                store.dispatch(
                    QualityControlActions.updateCentrePoleData(centerpoleData)
                );
                store.dispatch(
                    QualityControlActions.updateImageLoadingByIndex({
                        indices: [0, 1, 3, 4],
                        isLoading: true,
                    })
                );

                // from the adjacent poles in our dataset
                const adjacentPoles = data.filter(
                    (pole) => pole.pole === 'adjacent'
                );

                // and from the spans attached to the selected pole
                allAttachedSpans = mapAttachedSpans(selectedPole.span.selected);

                // store the attached spans in state
                const mappedAttachedSpans = mapAttachedSpans(allAttachedSpans);

                store.dispatch(
                    QualityControlActions.updateAdjacentSpans(
                        mappedAttachedSpans
                    )
                );

                // and attempt to render the outer entities.
                let backspan,
                    backpole,
                    backspanIndex,
                    forespan,
                    forepole,
                    forespanIndex,
                    getBackData,
                    getForeData;
                //console.log(selectedPole.span.selected)
                const availableSpans = [...selectedPole.span.selected].sort(
                    // drop spans last
                    (a, b) => {
                        if (!_isNil(a.pole_2) && _isNil(b.pole_2)) {
                            return -1;
                        } else if (_isNil(a.pole_2) && !_isNil(b.pole_2)) {
                            return 1;
                        } else {
                            return 0;
                        }
                    }
                );

                // if we have any spans, try to render the back span & pole first
                if (availableSpans.length > 0) {
                    backspan = availableSpans[0];
                    backpole = getAttachedPoleBySpan(backspan, adjacentPoles);
                    backspanIndex = mappedAttachedSpans.findIndex(
                        (s) => s.span_id == backspan.span_id
                    );

                    store.dispatch(
                        QualityControlActions.updateSpanIndex({
                            span: 'backspan',
                            index: backspanIndex,
                        })
                    );

                    getBackData = parseAttachedSpansAndPoles(
                        backpole,
                        backspan,
                        backpoleData,
                        backspanData,
                        cancelToken,
                        selectedPole
                    );

                    // then try to render the fore span and pole
                    if (availableSpans.length > 1) {
                        forespan = availableSpans[1];
                        forepole = getAttachedPoleBySpan(
                            forespan,
                            adjacentPoles
                        );
                        forespanIndex = mappedAttachedSpans.findIndex(
                            (s) => s.span_id == forespan.span_id
                        );

                        store.dispatch(
                            QualityControlActions.updateSpanIndex({
                                span: 'forespan',
                                index: forespanIndex,
                            })
                        );

                        getForeData = parseAttachedSpansAndPoles(
                            forepole,
                            forespan,
                            forepoleData,
                            forespanData,
                            cancelToken,
                            selectedPole
                        );
                    }

                    // call the endpoints in parallel, and continue when everything has processed.
                    await Promise.all([getBackData, getForeData]);
                }

                dispatchResults();

                centerpoleData = _cloneDeep(centerpoleData);

                extractLabelInfoFromCenterPole(
                    selectedPole,
                    centerpoleData,
                    forespan,
                    backspan,
                    selectedImageId
                );

                store.dispatch(
                    QualityControlActions.updateCentrePoleData(centerpoleData)
                );
            }
        }
    },
    cycleNextForePole: () => {
        const adjacentSpans =
            store.getState().qualityControl.selectedPoleAdjacentSpans;
        const currentForespanIndex =
            store.getState().qualityControl.forespanIndex;
        const currentBackspanIndex =
            store.getState().qualityControl.backspanIndex;

        if (adjacentSpans.length > 2) {
            let newForespanIndex = currentForespanIndex + 1;
            if (newForespanIndex >= adjacentSpans.length) {
                newForespanIndex = 0;
            }

            if (newForespanIndex === currentBackspanIndex) {
                newForespanIndex = newForespanIndex + 1;
            }

            if (newForespanIndex >= adjacentSpans.length) {
                newForespanIndex = 0;
            }

            QualityControlApi.updateSelectedPoleAndSpan(
                newForespanIndex,
                'fore'
            );
        }
    },
    cycleNextBackPole: () => {
        const adjacentSpans =
            store.getState().qualityControl.selectedPoleAdjacentSpans;
        const currentForespanIndex =
            store.getState().qualityControl.forespanIndex;
        const currentBackspanIndex =
            store.getState().qualityControl.backspanIndex;

        if (adjacentSpans.length > 2) {
            let newBackspanIndex = currentBackspanIndex + 1;
            if (newBackspanIndex >= adjacentSpans.length) {
                newBackspanIndex = 0;
            }

            if (newBackspanIndex === currentForespanIndex) {
                newBackspanIndex = newBackspanIndex + 1;
            }

            if (newBackspanIndex >= adjacentSpans.length) {
                newBackspanIndex = 0;
            }

            QualityControlApi.updateSelectedPoleAndSpan(
                newBackspanIndex,
                'back'
            );
        }
    },
    switchSpanIndicies: async () => {
        const currentForespanIndex =
            store.getState().qualityControl.forespanIndex;
        const currentBackspanIndex =
            store.getState().qualityControl.backspanIndex;

        // update back span
        const backspan = await QualityControlApi.updateSelectedPoleAndSpan(
            currentForespanIndex,
            'back',
            false
        );
        // update fore span
        const forespan = await QualityControlApi.updateSelectedPoleAndSpan(
            currentBackspanIndex,
            'fore',
            false
        );

        QualityControlApi.updateCenterPoleLabels({
            forespanId: forespan.span_id,
            backspanId: backspan.span_id,
        });
    },
    updateCenterPoleLabels: (poleOptions: {
        forespanId?: string;
        backspanId?: string;
    }) => {
        const rawData = store.getState().qualityControl.rawData;
        const rawCenterpoleData = rawData[0];

        const fsId =
            poleOptions.forespanId ??
            store.getState().qualityControl.foreSpan.span_id;

        const bsId =
            poleOptions.backspanId ??
            store.getState().qualityControl.backSpan.span_id;

        const forespan = rawCenterpoleData.span.selected.find(
            (s) => s.span_id === fsId
        );
        const backspan = rawCenterpoleData.span.selected.find(
            (s) => s.span_id === bsId
        );

        const currentCentrePoleData =
            store.getState().qualityControl.centrePole;
        const centrePoleData: IQcPole = _cloneDeep(currentCentrePoleData);

        centrePoleData.forwardLabels = [];
        centrePoleData.forwardNoHeightLabels = [];
        centrePoleData.backwardLabels = [];
        centrePoleData.backwardNoHeightLabels = [];

        extractLabelInfoFromCenterPole(
            rawCenterpoleData,
            centrePoleData,
            forespan,
            backspan
        );

        store.dispatch(
            QualityControlActions.updateCentrePoleData(centrePoleData)
        );
    },
    updateSelectedPoleAndSpan: async (
        spanIndex: number,
        type: 'fore' | 'back',
        updateCenterPole = true
    ) => {
        const cancelTokenKey =
            type === 'fore'
                ? GlobalAxiosCancelTokenKey.UPDATE_FORE_POLE_AND_SPAN
                : GlobalAxiosCancelTokenKey.UPDATE_BACK_POLE_AND_SPAN;

        cancelOutstandingQcRequests(cancelTokenKey);

        const cancelToken = addQcCancelToken(cancelTokenKey);

        const imageIndices: ImageIndex[] = type === 'fore' ? [3, 4] : [0, 1];
        store.dispatch(
            QualityControlActions.updateImageLoadingByIndex({
                indices: imageIndices,
                isLoading: true,
            })
        );

        const rawData = store.getState().qualityControl.rawData;

        const spanData: IQcSpanEntity = _cloneDeep(
            type === 'fore'
                ? initialQualityControlState.foreSpan
                : initialQualityControlState.backSpan
        );
        const poleData: IQcPole = _cloneDeep(
            type === 'fore'
                ? initialQualityControlState.forePole
                : initialQualityControlState.backPole
        );

        const adjacentPoles = rawData.filter(
            (pole) => pole.pole === 'adjacent'
        );

        const adjacentSpan =
            store.getState().qualityControl.selectedPoleAdjacentSpans[
                spanIndex
            ];

        const selectedSpan = rawData[0].span.selected.find(
            (s) => s.span_id === adjacentSpan.span_id
        );

        const selectedPole = getAttachedPoleBySpan(selectedSpan, adjacentPoles);

        await parseAttachedSpansAndPoles(
            selectedPole,
            selectedSpan,
            poleData,
            spanData,
            cancelToken,
            selectedPole
        );

        if (updateCenterPole) {
            const options =
                type === 'fore'
                    ? {
                          forespanId: spanData.span_id,
                      }
                    : {
                          backspanId: spanData.span_id,
                      };

            QualityControlApi.updateCenterPoleLabels(options);
        }

        if (type === 'fore') {
            store.dispatch(QualityControlActions.updateForePoleData(poleData));
            store.dispatch(QualityControlActions.updateForeSpanData(spanData));
        } else {
            store.dispatch(QualityControlActions.updateBackPoleData(poleData));
            store.dispatch(QualityControlActions.updateBackSpanData(spanData));
        }
        store.dispatch(
            QualityControlActions.updateSpanIndex({
                span: type === 'fore' ? 'forespan' : 'backspan',
                index: spanIndex,
            })
        );
        store.dispatch(
            QualityControlActions.updateImageLoadingByIndex({
                indices: imageIndices,
                isLoading: false,
            })
        );

        return spanData;
    },
    updateSvgImageIndex: (imageIndex: ImageIndex, newIndex: ImageIndex) => {
        store.dispatch(
            QualityControlActions.updateSvgImageIndex({
                imageIndex: imageIndex,
                newIndex: newIndex,
            })
        );
    },
    updateAreImagesLoading: (imageBayIndex: ImageIndex, isLoading: boolean) => {
        store.dispatch(
            QualityControlActions.updateImageLoadingByIndex({
                indices: imageBayIndex,
                isLoading: isLoading,
            })
        );
    },
    updateSingleImageUrlInTruesizeArray: async (
        photoPositionIndex: ImageIndex,
        imageIndex: ImageIndex,
        imageId: string
    ) => {
        const imgResp = await entityEndPoints.getEntityImage(imageId, false);

        if (imgResp.isSuccess) {
            const img = blobToUrl(imgResp.data);
            findAndUpdateUrl(photoPositionIndex, imageIndex, img);
        }
    },
    updateSingleImageUrlInPoletagArray: async (imageIndex, imageId) => {
        const imgResp = await entityEndPoints.getEntityImage(imageId, false);
        if (imgResp.isSuccess) {
            const img = blobToUrl(imgResp.data);

            const currentCentrePole =
                store.getState().qualityControl.centrePole;
            const currentPoletagUrls = currentCentrePole.poletagImageUrls;
            const newPoleTagUrlArray = currentPoletagUrls.map(
                (element, index) => {
                    const value =
                        index === imageIndex
                            ? { ...element, url: img }
                            : element;
                    return value;
                }
            );

            store.dispatch(
                QualityControlActions.updateCentrePoleData({
                    ...currentCentrePole,
                    poletagImageUrls: newPoleTagUrlArray,
                })
            );
        }
    },
    getReworkStageTopicReasons: async () => {
        const response =
            await qualityControlEndpoints.getReworkStageTopicReason();

        if (response.isSuccess) {
            const stages = response.data.data.stages as IReworkReasonStage[];

            store.dispatch(
                QualityControlActions.updateReworkStageTopicReasons(stages)
            );
            return stages;
        }
    },
    addReworkReason: (reworkReason: IReworkReason, comment: string) => {
        const analysisEntity =
            store.getState().qualityControl.selectedAnalysisEntity;
        const clonedEntity = _cloneDeep(analysisEntity);
        const clonedReason = cloneReasonAndAddComment(reworkReason, comment);
        clonedEntity.reworkReasons.push(clonedReason);

        store.dispatch(
            QualityControlActions.updateSelectedAnalysisEntity({
                entity: clonedEntity,
            })
        );
    },
    updateReworkReason: (
        existingReason: IReworkReason,
        reworkReason: IReworkReason,
        comment: string
    ) => {
        const analysisEntity =
            store.getState().qualityControl.selectedAnalysisEntity;
        const clonedEntity = _cloneDeep(analysisEntity);
        const reworkIndex = clonedEntity.reworkReasons.findIndex(
            (r) => r.id === existingReason.id
        );
        clonedEntity.reworkReasons[reworkIndex] = cloneReasonAndAddComment(
            reworkReason,
            comment
        );

        store.dispatch(
            QualityControlActions.updateSelectedAnalysisEntity({
                entity: clonedEntity,
            })
        );
    },
    removeReworkReason: (reason: IReworkReason) => {
        const analysisEntity =
            store.getState().qualityControl.selectedAnalysisEntity;
        const clonedEntity = _cloneDeep(analysisEntity);
        const reworkIndex = clonedEntity.reworkReasons.findIndex(
            (r) => r.id === reason.id
        );
        clonedEntity.reworkReasons.splice(reworkIndex, 1);

        store.dispatch(
            QualityControlActions.updateSelectedAnalysisEntity({
                entity: clonedEntity,
            })
        );
    },
    getNextEntity: () => {
        const analysisEntity =
            store.getState().qualityControl.selectedAnalysisEntity;
        const clonedEntity = _cloneDeep(analysisEntity);
        clonedEntity.qcState =
            clonedEntity.reworkReasons.length > 0
                ? AnalysisEntityState.REWORK
                : AnalysisEntityState.PASS;

        store.dispatch(
            QualityControlActions.updateSelectedAnalysisEntity({
                entity: clonedEntity,
                keepState: true,
            })
        );

        // shuffle entities and find next unresolved entity
        const allEntities = store.getState().qualityControl.analysisEntities;
        const shuffled = shuffleAnalysisEntities(allEntities);
        const nextEntity = shuffled.find(
            (e) => e.qcState == AnalysisEntityState.TODO
        );
        if (!_isNil(nextEntity)) {
            QualityControlApi.setSelectedAnalysisEntity(nextEntity);
        }
    },
    getQcStatusOptions: async (entityVersionId: string) => {
        const response = await qualityControlEndpoints.getQcStatuses(
            entityVersionId
        );

        if (response.isSuccess) {
            const statuses = response.data.data;
            if (!Array.isArray(statuses) || statuses.length === 0) {
                statuses.push({
                    id: 0,
                    name: 'ERROR',
                    display_name: 'ERROR - NO DATA RETURNED',
                });
            }

            store.dispatch(
                QualityControlActions.updateQcStatusOptions(statuses)
            );
        }
    },
    submitQcRun: async (
        selectedFolderId: string,
        selectedFolderName: string,
        selectedProjectId: number,
        userId: string,
        username: string,
        startAnalystId: string,
        startAnalystName: string,
        endAnalystId: string,
        endAnalystName: string,
        isAllPass: boolean,
        startStatus: string,
        endStatus: string,
        targetPassYield: number,
        actualYield: number,
        targetInspectionRate: number,
        actualInspectionRate: number
    ) => {
        const entities = store.getState().qualityControl.analysisEntities;

        const mappedCompletedEntities = entities
            .filter((e) => e.qcState !== AnalysisEntityState.TODO)
            .map((e) => {
                return {
                    entity_version_id: e.entity_version_id,
                    QC_result:
                        e.qcState === AnalysisEntityState.PASS
                            ? 'pass'
                            : 'rework',
                    QC_record:
                        !_isNil(e.reworkReasons) && e.reworkReasons.length > 0
                            ? e.reworkReasons.map((r) => ({
                                  QC_rework_reason: r.id,
                                  comment: r.comment,
                              }))
                            : [],
                };
            });

        const inspectionStart =
            store.getState().qualityControl.inspectionStartDate;
        const inspectionEnd = new Date().toISOString();
        const adjustedEndAnalystId = !_isNil(endAnalystId)
            ? endAnalystId.replace('|', '.')
            : endAnalystId;
        const inspectionNamePrefix = isAllPass ? 'pass' : 'rework';

        // TODO PERSISTENCE: send elapsed time
        const persistedData = store
            .getState()
            .browserPersistence.qcRuns.find(
                (q) => q.folder_id === selectedFolderId
            );

        const elapsedTime = getUpdatedElapsedTime(persistedData);

        const payload = {
            QC_inspection_name: `QC_${inspectionNamePrefix}_${selectedFolderName}_${getTimestampStr()}`,
            qc_analyst_id: userId.replace('|', '.'),
            qc_analyst_name: username,
            ike_analyst_name: startAnalystName,
            ike_analyst_id: startAnalystId,
            end_analyst_id: adjustedEndAnalystId,
            end_analyst_name: endAnalystName,
            qc_inspection_start: inspectionStart,
            qc_inspection_end: inspectionEnd,
            folder_fk: selectedFolderId,
            project_fk: selectedProjectId,
            total_entities: entities.length,
            number_passed: entities.filter(
                (e) => e.qcState === AnalysisEntityState.PASS
            ).length,
            number_rework: entities.filter(
                (e) => e.qcState === AnalysisEntityState.REWORK
            ).length,
            QC_result_array: mappedCompletedEntities,
            is_final_batch: true,
            is_all_pass: isAllPass,
            status: _isNil(startStatus) ? 'NA' : startStatus,
            end_status: _isNil(endStatus) ? 'NA' : endStatus,
            target_pass_yield: targetPassYield,
            actual_yield: actualYield,
            target_inspection_rate: targetInspectionRate,
            inspection_rate: actualInspectionRate,
            report_url: 0,
            elapsed_time: elapsedTime,
        };

        const response = await qualityControlEndpoints.uploadQcResults(payload);

        if (response.statusCode === 200) {
            BrowserPersistenceApi.removePersistedQcRun(selectedFolderId);
            return response.isSuccess;
        } else {
            error('failed to save qc run');
            return false;
        }
    },
};
